import { useCallback } from "react";
import {
  QueryFunctionContext,
  QueryKey,
  UseInfiniteQueryOptions,
  UseInfiniteQueryResult,
  // eslint-disable-next-line no-restricted-imports
  useInfiniteQuery as useInfiniteQueryReactQuery,
} from "react-query";

import { HttpError } from "@/browser";
import { IPageable } from "@/core/api";

export type InfiniteFetchFunction<R, P> = (params: P) => Promise<IPageable<R[]>>;
export type InfiniteResultState<R, E> = UseInfiniteQueryResult<IPageable<R[]>, HttpError<E>> & {
  queryKey: QueryKey;
};
export type InfiniteFetchOptions<R, E> = UseInfiniteQueryOptions<
  IPageable<R[]>,
  HttpError<E>,
  IPageable<R[]>,
  IPageable<R[]>,
  QueryKey
>;

const initialParams = { page: 0, size: 30 };

/*
  NOTE: Cancel signals are not supported by this hook because AxiosRequestConfig.signal is not supported by axios 0.21. 
  Upgrading axios may cause problems with cancelToken which is used extensively in this project.
*/

export const useInfiniteQuery = <R, P, E = unknown>(
  queryKey: QueryKey,
  queryFunction: InfiniteFetchFunction<R, P>,
  params: P,
  options?: InfiniteFetchOptions<R, E>,
): InfiniteResultState<R, E> => {
  const localQueryKey = [queryKey, params];

  const fetchData = useCallback(
    async ({ pageParam: page = 0 }: QueryFunctionContext) => {
      return queryFunction({ ...initialParams, ...params, page });
    },
    [params, queryFunction],
  );

  const query = useInfiniteQueryReactQuery<IPageable<R[]>, HttpError<E>>(localQueryKey, fetchData, {
    suspense: false,
    getNextPageParam: lastPage => {
      if (lastPage.last) {
        return;
      }
      return lastPage.number + 1;
    },
    ...options,
  });

  return { ...query, queryKey: localQueryKey };
};
